import { useState, useCallback, useEffect } from 'react'
import { api } from '../utils/api'
import toast from 'react-hot-toast'

export interface Device {
  name: string
  type: string
  u_height: number
  weight_kg: number
  power_watts: number
}

export interface Recommendation {
  id: number
  device_name: string
  rack_label: string
  rack_row: number
  rack_index: number
  rack_id: number
  u_position: number
  u_height: number
  reason: string
  confidence: number
}

export interface PlacementResult {
  dc_code: string
  recommendations: Recommendation[]
  total_devices: number
  placed_devices: number
  failed_devices: Array<{ device: string; reason: string }>
}

export function usePlacement(initialDcCode: string) {
  const [dcCode, setDcCode] = useState(initialDcCode)
  const [devices, setDevices] = useState<Device[]>([])
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PlacementResult | null>(null)
  const [dcList, setDcList] = useState<Array<{ code: string; name: string }>>([])

  useEffect(() => {
    setDcCode(initialDcCode)
  }, [initialDcCode])

  useEffect(() => {
    api.get('/api/datacenters').then(res => {
      const dcs = res.data as Array<{ code: string; name: string }>
      setDcList(dcs)
      if (dcs.length) {
        const match = dcs.find(d => d.code.toLowerCase() === (initialDcCode || '').toLowerCase())
        if (match) {
          setDcCode(match.code)
        } else if (!initialDcCode) {
          setDcCode(dcs[0].code)
        }
      }
    }).catch(err => toast.error('Ошибка загрузки ЦОД: ' + (err.response?.data?.detail || err.message)))
  }, [initialDcCode])

  const addDevice = useCallback((device: Device) => {
    setDevices(prev => [...prev, device])
  }, [])

  const removeDevice = useCallback((index: number) => {
    setDevices(prev => prev.filter((_, idx) => idx !== index))
  }, [])

  const calculatePlacement = useCallback(async () => {
    if (devices.length === 0 || !dcCode) return
    setLoading(true)
    try {
      const res = await api.post(`/api/datacenters/${dcCode}/placement-plan`, {
        dc_code: dcCode,
        devices,
        constraints: { phase_balance: true },
      })
      const data = res.data as PlacementResult
      setResult(data)
      setRecommendations(data.recommendations.map((r, i) => ({ ...r, id: i })))
    } catch (err: any) {
      toast.error('Ошибка расчёта: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }, [dcCode, devices])

  const applyPlacement = useCallback(async () => {
    if (!result) return false
    try {
      await api.post(`/api/datacenters/${dcCode}/placement-apply`, {
        dc_code: dcCode,
        devices,
        recommendations: result.recommendations,
      })
      toast.success('Устройства успешно размещены в стойках!')
      setDevices([])
      setResult(null)
      setRecommendations([])
      return true
    } catch (err: any) {
      toast.error('Ошибка при применении: ' + (err.response?.data?.detail || err.message))
      return false
    }
  }, [dcCode, devices, result])

  return {
    dcCode,
    setDcCode,
    dcList,
    devices,
    addDevice,
    removeDevice,
    setDevices,
    recommendations,
    result,
    loading,
    calculatePlacement,
    applyPlacement,
  }
}
